<script setup lang="ts">
  import { defineProps, type PropType, computed } from 'vue'
  import { ExclamationCircleIcon } from '@heroicons/vue/24/outline'
  import { type RuleExpression, useField } from 'vee-validate'
  import { useThemeColor } from '@/composables/useThemeColorV3'

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    id: {
      type: String,
      required: false
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: String,
      required: false,
      default: ''
    },
    rows: {
      type: [String, Number],
      required: false,
      default: 3
    },
    placeholder: {
      type: String,
      required: false,
      default: ''
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    heightFull: {
      type: Boolean,
      required: false,
      default: false
    },
    dataCy: {
      type: String,
      required: false,
      default: ''
    },
    // New props for enhanced styling (backward compatible)
    useModernStyling: {
      type: Boolean,
      required: false,
      default: false
    }
  })

  const emit = defineEmits(['update:modelValue', 'input', 'blur'])

  const {
    value,
    errorMessage,
    handleBlur: veeHandleBlur,
    handleChange
  } = useField(props.name, props.rules, {
    syncVModel: true
  })

  const themeColor = computed(() => useThemeColor().value)

  const handleInput = (event: Event) => {
    const target = event.target as HTMLTextAreaElement
    value.value = target.value
    handleChange(event)
    emit('update:modelValue', target.value)
    emit('input', target.value)
  }

  const handleBlur = (event: FocusEvent) => {
    veeHandleBlur(event)
    emit('blur', event)
  }
</script>

<template>
  <!-- Modern styling variant (new branch) -->
  <div
    v-if="useModernStyling"
    class="mb-4"
  >
    <label
      v-if="label"
      :for="props.id || props.name"
      class="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"
    >
      {{ label }}
    </label>
    <textarea
      :id="props.id || props.name"
      v-model="value"
      :name="props.name"
      :placeholder="placeholder"
      :disabled="disabled"
      :rows="rows"
      :data-cy="dataCy"
      class="block w-full rounded-lg border px-3 py-2 text-sm text-gray-800 placeholder:text-gray-500
        disabled:cursor-not-allowed disabled:bg-gray-100 dark:bg-neutral-700 dark:text-gray-100
        dark:placeholder:text-gray-400"
      :class="
        errorMessage
          ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
          : 'border-gray-200 focus:border-purple-600 focus:ring-purple-600 dark:border-neutral-600'
      "
      @input="handleInput"
      @blur="handleBlur"
    />
    <div
      v-if="errorMessage"
      class="mt-1 flex items-center gap-1 text-xs text-red-600"
    >
      <ExclamationCircleIcon class="h-4 w-4" />
      {{ errorMessage }}
    </div>
  </div>

  <!-- Original styling (master/legacy) -->
  <div
    v-else
    class="flex flex-col"
  >
    <label
      v-if="label"
      class="mb-2 block text-sm font-medium"
    >
      {{ label }}
    </label>
    <div :class="[errorMessage ? 'relative' : '', heightFull ? 'h-full' : '']">
      <textarea
        :id="props.id || props.name"
        v-model="value"
        class="block h-full w-full rounded-lg px-3 py-2 text-sm text-gray-800 placeholder:text-gray-500 focus:z-10"
        :name="props.name"
        :class="
          errorMessage
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : `border-gray-200 focus:border-${themeColor}-600 focus:ring-${themeColor}-600`
        "
        :rows="rows"
        :placeholder="placeholder"
        :disabled="props.disabled"
        :data-cy="dataCy"
        @input="handleInput"
        @change="handleChange"
        @blur="handleBlur"
      ></textarea>
      <div
        v-if="errorMessage"
        class="pointer-events-none absolute end-0 top-0 flex items-center p-3"
      >
        <ExclamationCircleIcon class="size-4 flex-shrink-0 text-red-500" />
      </div>
    </div>
    <p
      v-if="errorMessage"
      class="mt-1 text-xs text-red-600"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>

<style scoped>
  textarea:disabled {
    background-color: #f9f9f9; /* Light grey background */
    color: #aaa; /* Gray text */
    cursor: not-allowed; /* Not allowed cursor */
  }
</style>
